import { useCallback, useEffect, useState } from "react";
import {
  Measurement,
  type NewMeasurement,
  type PatchMeasurement,
} from "@jackedlogs/shared";
import { apiFetch } from "./api";

// The web console's measurements resource (/api/me/measurements). Online
// only — no offline outbox (that stays on mobile); a failed write returns
// false and the shared Form keeps the typed values + surfaces a notice.
// Same endpoints + payloads as the mobile useMeasurements, so a write
// round-trips the exact schema the API already accepts.

type ListResponse = { items: unknown[]; cursor: string | null };

const newId = (): string =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.round(Math.random() * 1e9).toString(36)}`;

export type UseMeasurementsWeb = {
  items: Measurement[];
  loading: boolean;
  loadFailed: boolean;
  create: (payload: NewMeasurement) => Promise<boolean>;
  update: (id: string, patch: PatchMeasurement) => Promise<boolean>;
  remove: (id: string) => Promise<boolean>;
};

export function useMeasurementsWeb(): UseMeasurementsWeb {
  const [items, setItems] = useState<Measurement[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const out: Measurement[] = [];
      let cursor: string | null = null;
      do {
        const q: string = cursor ? `?cursor=${encodeURIComponent(cursor)}` : "";
        const res: ListResponse = await apiFetch<ListResponse>(`/api/me/measurements${q}`);
        for (const raw of res.items ?? []) {
          const p = Measurement.safeParse(raw);
          if (p.success) out.push(p.data);
        }
        cursor = res.cursor;
      } while (cursor);
      setItems(out);
      setLoadFailed(false);
    } catch {
      setItems((prev) => prev ?? []);
      setLoadFailed(true);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const create = useCallback(
    async (payload: NewMeasurement) => {
      const body: NewMeasurement = { ...payload, id: payload.id ?? newId() };
      try {
        await apiFetch<Measurement>("/api/me/measurements", { method: "POST", body });
        await refresh();
        return true;
      } catch {
        return false;
      }
    },
    [refresh],
  );

  const update = useCallback(
    async (id: string, patch: PatchMeasurement) => {
      try {
        await apiFetch<Measurement>(`/api/me/measurements/${id}`, {
          method: "PATCH",
          body: patch,
        });
        await refresh();
        return true;
      } catch {
        return false;
      }
    },
    [refresh],
  );

  const remove = useCallback(
    async (id: string) => {
      try {
        await apiFetch<unknown>(`/api/me/measurements/${id}`, { method: "DELETE" });
        await refresh();
        return true;
      } catch {
        return false;
      }
    },
    [refresh],
  );

  return {
    items: items ?? [],
    loading: items === null && !loadFailed,
    loadFailed,
    create,
    update,
    remove,
  };
}
